import React from "react";
import { AlertOctagon, AlertTriangle, CheckCircle2, Info, MinusCircle, FileJson } from "lucide-react";

const ESTILO = {
  BLOQUEO: { Icon: AlertOctagon, cls: "text-destructive", etiqueta: "Bloqueo" },
  ADVERTENCIA: { Icon: AlertTriangle, cls: "text-amber-700 dark:text-amber-400", etiqueta: "Advertencia" },
  INFO: { Icon: Info, cls: "text-sky-700 dark:text-sky-400", etiqueta: "Informativa" },
  OK: { Icon: CheckCircle2, cls: "text-emerald-700 dark:text-emerald-400", etiqueta: "OK" },
  OMITIDA: { Icon: MinusCircle, cls: "text-muted-foreground", etiqueta: "Omitida" },
};
const ORDEN = { BLOQUEO: 0, ADVERTENCIA: 1, INFO: 2, OK: 3, OMITIDA: 4 };
const MES = ["enero", "febrero", "marzo", "abril", "mayo", "junio", "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre"];
const FUENTE = { ventas: "ventas", compras: "compras", banco: "banco", remuneraciones: "sueldos" };
const mesTxt = (p) => (p ? `${MES[+p.slice(5, 7) - 1]} ${p.slice(0, 4)}` : "ninguno");

export default function PanelValidaciones({ M, P, setP, X, readOnly, onExportar }) {
  if (!M) return null;
  const vs = [...(M.validaciones || [])].sort((a, b) => ORDEN[a.estado] - ORDEN[b.estado] || a.id.localeCompare(b.id));
  const cent = X?.contabilidad?.centralizacion;
  const nb = M.bloqueos.length;
  const na = M.advertencias.length;
  const set = (patch) => setP((prev) => ({ ...prev, ...patch }));

  return (
    <div className="space-y-3" data-testid="informe-validaciones">
      <div
        className={`rounded-sm px-2.5 py-2 text-[12px] ${
          nb
            ? "bg-destructive/10 text-destructive"
            : na
              ? "bg-amber-50 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300"
              : "bg-emerald-50 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300"
        }`}
      >
        {nb
          ? `${nb} ${nb === 1 ? "bloqueo" : "bloqueos"}: no se puede emitir el informe al cliente. La hoja de trabajo sí.`
          : na
            ? `${na} ${na === 1 ? "advertencia" : "advertencias"}: se puede emitir; el informe muestra las notas que correspondan.`
            : "Sin bloqueos ni advertencias."}
      </div>

      <ul className="space-y-2">
        {vs.map((v) => {
          const { Icon, cls, etiqueta } = ESTILO[v.estado] || ESTILO.OMITIDA;
          return (
            <li key={v.id} className="text-[11.5px] leading-snug" data-testid={`validacion-${v.id}`}>
              <div className={`flex items-start gap-1.5 ${cls}`}>
                <Icon className="h-3.5 w-3.5 shrink-0 mt-px" />
                <span className="font-semibold">
                  {v.id} · {v.control}
                </span>
                <span className="ml-auto shrink-0 text-[10px] uppercase tracking-wide">{etiqueta}</span>
              </div>
              {v.estado !== "OK" && v.estado !== "OMITIDA" && (
                <div className="pl-5 mt-0.5 text-muted-foreground">
                  {v.mensajeInterno}
                  {v.accion && <div className="text-foreground mt-0.5">→ {v.accion}</div>}
                </div>
              )}
              {v.id === "V03" && v.estado === "ADVERTENCIA" && M.rk === "14D3" && (
                <label className="flex items-start gap-2 pl-5 mt-1 text-foreground">
                  <input
                    type="checkbox"
                    className="mt-0.5"
                    checked={!!P.excluirImpagas}
                    disabled={readOnly}
                    onChange={(e) => set({ excluirImpagas: e.target.checked })}
                  />
                  Confirmo que siguen impagas: no rebajarlas en la proyección
                </label>
              )}
            </li>
          );
        })}
      </ul>

      <label className="block text-[11.5px] text-muted-foreground">
        Contabilidad cerrada hasta (último mes completo)
        <input
          type="month"
          value={P.ultimoPeriodoCentralizado || cent?.ultimo || ""}
          disabled={readOnly}
          onChange={(e) => set({ ultimoPeriodoCentralizado: e.target.value })}
          className="w-full mt-1 px-2.5 py-1.5 border border-border rounded-sm text-[13px] bg-background text-foreground focus:outline-none focus:ring-1 focus:ring-primary/40 disabled:opacity-60"
          data-testid="informe-upc"
        />
        <span className="block mt-1 text-[11px] leading-snug">
          {cent
            ? `Según Aequor: ${mesTxt(cent.ultimo)}${
                Object.keys(cent.fuentes || {}).length
                  ? ` (${Object.entries(cent.fuentes)
                      .map(([k, f]) => `${FUENTE[k] || k} ${f.ultimo ? MES[+f.ultimo.slice(5, 7) - 1].slice(0, 3) : "—"}`)
                      .join(", ")})`
                  : ""
              }.`
            : "Sin datos de centralización en Aequor."}
          {P.ultimoPeriodoCentralizado && !readOnly && (
            <button type="button" className="ml-1 text-primary hover:underline" onClick={() => set({ ultimoPeriodoCentralizado: "" })}>
              Usar el de Aequor
            </button>
          )}
        </span>
      </label>

      {M.E && (
        <button
          type="button"
          onClick={onExportar}
          className="inline-flex items-center gap-1.5 text-[11.5px] font-semibold text-primary hover:underline"
          data-testid="informe-exportar-e"
        >
          <FileJson className="h-3.5 w-3.5" /> Exportar datos ERP (JSON v{M.E.version})
        </button>
      )}
    </div>
  );
}
